import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { APP_VERSION } from '@/lib/version';
import {
  Pulse as Activity,
  Bell,
  BellRinging as BellRing,
  BookOpen,
  Brain,
  Broadcast as Radio,
  Bug,
  Calendar,
  CaretRight as ChevronRight,
  ChartBar as BarChart3,
  ClipboardText as ClipboardList,
  Compass,
  Crown,
  Database,
  Desktop,
  DownloadSimple as Download,
  Envelope as Mail,
  Eye,
  FileMagnifyingGlass as FileSearch,
  FileText,
  FolderOpen,
  GearSix as Settings,
  GitBranch,
  Globe,
  Graph as Network,
  HardDrives as Server,
  Heartbeat as HeartPulse,
  House as Home,
  Key,
  Lightning as Zap,
  Lightning as LightningIcon,
  List as Menu,
  Lock,
  MagnifyingGlass as Search,
  Monitor,
  Plug,
  Package,
  ClockClockwise,
  Question as HelpCircle,
  Scales as Scale,
  Scan,
  Shield,
  ShieldCheck,
  ShieldWarning as ShieldAlert,
  SidebarSimple as PanelLeftClose,
  SidebarSimple as PanelLeft,
  SignOut as LogOut,
  Sparkle,
  Target,
  TreeStructure,
  TrendUp as TrendingUp,
  UserGear as UserCog,
  Users,
  Wrench,
  X
} from '@phosphor-icons/react';
// v4: los antiguos OnyxIcons (Network/ADAttacks/EDR/EmailSecurity) eran SVG
// con cyan hardcodeado + gradientes + glow (tells "diseño IA" que el usuario
// rechaza). Sustituidos por iconos Phosphor monocromos (currentColor, línea)
// coherentes con el resto del sidebar v4.
const NetworkIcon = Network;          // topología de red (Graph)
const ADAttacksIcon = ShieldAlert;    // ataque a Active Directory (ShieldWarning)
const EDRIcon = Desktop;              // endpoint detection & response
const EmailSecurityIcon = Mail;       // seguridad de correo (Envelope)
import apiClient from '@/lib/axiosInstance';
import ThemeSwitcher from './ThemeSwitcher';
import NotificationCenter from './NotificationCenter';
import ClientSwitcher from './ClientSwitcher';
import { ViewModeToggle } from './ViewModeToggle';
import { getBackendUrl } from '@/lib/api';
import { useUserRole } from '@/lib/userRole';
import { parseApiError } from '@/lib/utils';
import { useSidebar } from '@/contexts/SidebarContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import TourLauncher from './TourLauncher';

const BACKEND_URL = getBackendUrl();
// apiClient ya tiene baseURL=${BACKEND_URL}/api — paths van relativos.
// Concatenar otro /api aquí duplica el prefix con dominios relativos
// (ej. REACT_APP_BACKEND_URL='' produce /api/api/...).

const Navigation = ({ onStartTour }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { sidebarCollapsed, setSidebarCollapsed } = useSidebar();
  const { t } = useLanguage();
  const [openMenu, setOpenMenu] = useState(null);
  const [stats, setStats] = useState({ agents: 0, criticalVulns: 0 });
  const [statsError, setStatsError] = useState(null);
  // El rol se deriva de GET /api/auth/me (useUserRole). Antes se leía de
  // sessionStorage una sola vez, dentro de un useEffect con []: en una pestaña
  // nueva el token seguía vivo y el rol no, así que el menú escondía las
  // entradas de administrador sin ningún aviso.
  const userRole = useUserRole();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadStats = async () => {
    try {
      const response = await apiClient.get('/dashboard/stats');
      setStats({
        agents: response.data.active_agents || 0,
        criticalVulns: response.data.critical_vulnerabilities || 0
      });
      setStatsError(null);
    } catch (error) {
      // Antes se callaba y la barra seguía diciendo "sistema en línea" con 0 agentes.
      setStatsError(parseApiError(error.response?.data?.detail, 'No se pudo contactar con el servidor'));
    }
  };

  // Jerarquía de roles (QA #6): user < viewer < analyst < admin.
  // Coherente con ROLE_RANK de ProtectedRoute. Las entradas SOC se marcan con
  // `minRole` para ocultarlas al rol `user` (lectura SOC = mínimo viewer).
  const ROLE_RANK = { user: 1, viewer: 2, analyst: 3, admin: 4, superadmin: 5 };
  const hasMinRole = (minRole) => {
    if (!minRole) return true;
    const have = ROLE_RANK[userRole] || 0;
    const need = ROLE_RANK[minRole] || 0;
    // NULL-safe: si el rol del usuario es desconocido (0), no ocultamos
    // (el backend sigue devolviendo 403). Solo ocultamos cuando sabemos el rol.
    if (have === 0) return true;
    return have >= need;
  };

  const menuItems = [
    { type: 'divider', label: t('nav.overview') },
    {
      type: 'link',
      path: '/',
      label: t('nav.dashboard'),
      icon: Home
    },
    {
      type: 'link',
      path: '/security-roadmap',
      label: t('nav.securityRoadmap', 'Tu seguridad'),
      icon: Compass,
      isNew: true,
      minRole: 'viewer'
    },
    {
      type: 'link',
      path: '/threat-model',
      label: t('nav.threatModel', 'Modelado de amenazas'),
      icon: TreeStructure,
      isNew: true,
      minRole: 'viewer'
    },
    {
      type: 'link',
      path: '/threat-map',
      label: t('nav.threatMap'),
      icon: Globe,
      isNew: true
    },
    { type: 'divider', label: t('nav.operations') },
    {
      type: 'dropdown',
      label: t('nav.infrastructure'),
      icon: Monitor,
      items: [
        { path: '/agents', label: t('nav.agents'), icon: Server, badge: stats.agents },
        { path: '/agent-health', label: t('nav.agentHealth'), icon: HeartPulse },
        { path: '/agent-groups', label: t('nav.agentGroups'), icon: FolderOpen },
        { path: '/network', label: t('nav.networkMap'), icon: NetworkIcon },
        { path: '/network-scanner', label: t('nav.scanner'), icon: Search },
        { path: '/ingestion-stats', label: t('nav.ingestionPipeline'), icon: LightningIcon, isNew: true },
        { path: '/certificate-management', label: t('nav.certificates'), icon: ShieldCheck, isNew: true },
      ]
    },
    {
      type: 'dropdown',
      label: t('nav.security'),
      icon: ShieldAlert,
      items: [
        { path: '/vulnerabilities', label: t('nav.vulnerabilities'), icon: Shield, badge: stats.criticalVulns, badgeColor: 'red', minRole: 'viewer' },
        { path: '/threat-intel', label: t('nav.threatIntel'), icon: Search, minRole: 'viewer' },
        { path: '/ioc-management', label: t('nav.iocManager'), icon: Database, minRole: 'viewer' },
        { path: '/edr', label: t('nav.edr'), icon: EDRIcon, minRole: 'viewer' },
        { path: '/antivirus', label: t('nav.antivirus'), icon: Bug, minRole: 'viewer' },
        { path: '/inventory', label: t('nav.inventory', 'Inventario'), icon: Package, isNew: true, minRole: 'viewer' },
        { path: '/dlp', label: t('nav.dlp'), icon: FileSearch, minRole: 'viewer' },
        { path: '/ad-attacks', label: t('nav.adAttacks'), icon: ADAttacksIcon, minRole: 'viewer' },
        { path: '/attack-library', label: t('nav.attackLibrary'), icon: Target, isNew: true, minRole: 'viewer' },
        { path: '/attack-library/runs', label: t('nav.runHistory'), icon: ClockClockwise, isNew: true, minRole: 'viewer' },
        { path: '/packs', label: t('nav.attackPacks'), icon: Package, isNew: true, minRole: 'viewer' },
        { path: '/attack-paths', label: t('nav.attackPaths'), icon: TreeStructure, isNew: true, minRole: 'viewer' },
        { path: '/ai-mutation', label: t('nav.aiMutation'), icon: Brain, isNew: true, minRole: 'viewer' },
        { path: '/cti-inbox', label: t('nav.ctiInbox', 'CTI Auto-Ingest'), icon: FileText, isNew: true, minRole: 'viewer' },
        { path: '/email-security', label: t('nav.emailSecurity'), icon: EmailSecurityIcon, isNew: true, minRole: 'viewer' },
      ]
    },
    {
      type: 'dropdown',
      label: t('nav.detection'),
      icon: Scan,
      items: [
        { path: '/sigma-rules', label: t('nav.sigmaRules'), icon: FileText, isNew: true, minRole: 'viewer' },
        { path: '/yara-rules', label: t('nav.yaraRules'), icon: Shield, isNew: true, minRole: 'viewer' },
        { path: '/correlation', label: t('nav.correlation'), icon: GitBranch, isNew: true, minRole: 'viewer' },
        { path: '/mitre-coverage', label: t('nav.mitreCoverage'), icon: Target, isNew: true, minRole: 'viewer' },
        { path: '/bas', label: t('nav.basValidation'), icon: ShieldCheck, isNew: true, minRole: 'viewer' },
        { path: '/external-siem', label: t('nav.externalSiem', 'External SIEM'), icon: Plug, isNew: true, minRole: 'viewer' },
      ]
    },
    { type: 'divider', label: t('nav.detectRespond') },
    {
      type: 'dropdown',
      label: t('nav.response'),
      icon: Bell,
      items: [
        { path: '/alerts', label: t('nav.alerts'), icon: BellRing, minRole: 'viewer' },
        // Retirada T5 (2026-10-13): IncidentResponse y HumanLayer fuera.
        // La gestion de incidentes vive en /alerts (pestana "Incidentes").
        { path: '/remediation', label: t('nav.remediation'), icon: Wrench, minRole: 'viewer' },
        { path: '/data-leaks', label: t('nav.dataLeaks'), icon: Database, minRole: 'viewer' },
      ]
    },
    {
      type: 'dropdown',
      label: t('nav.monitoring'),
      icon: BarChart3,
      items: [
        { path: '/metrics', label: t('nav.metrics'), icon: Activity },
        { path: '/mini-siem', label: t('nav.miniSiem'), icon: Eye, isNew: true, minRole: 'viewer' },
        { path: '/reports', label: t('nav.reports'), icon: FileText, minRole: 'viewer' },
        { path: '/scheduled-reports', label: t('nav.scheduledReports'), icon: Calendar, minRole: 'viewer' },
        { path: '/siem-integrations', label: t('nav.siemExport'), icon: Radio, minRole: 'viewer' },
        { path: '/compliance', label: t('nav.compliance'), icon: Scale, minRole: 'viewer' },
        { path: '/compliance-drift', label: t('nav.complianceDrift'), icon: TrendingUp, isNew: true, minRole: 'viewer' },
      ]
    },
    { type: 'divider', label: t('nav.system') },
    {
      type: 'dropdown',
      label: t('nav.admin'),
      icon: UserCog,
      adminOnly: true,
      items: [
        { path: '/users', label: t('nav.users'), icon: Users },
        { path: '/settings', label: t('nav.settings'), icon: Settings },
        { path: '/platform-config', label: t('nav.infrastructure'), icon: Server },
        { path: '/sso', label: t('nav.sso'), icon: Lock },
        { path: '/license', label: t('nav.license'), icon: Crown },
        { path: '/integrations', label: t('nav.connectors'), icon: Plug },
        { path: '/ip-filter', label: t('nav.ipFilter'), icon: Shield },
        // La retención la gestiona el PROVEEDOR: la política es global y el
        // backend exige superadmin. A un administrador de cliente se le ocultaba
        // nada y le daba 403 en cada botón.
        { path: '/data-retention', label: t('nav.dataRetention'), icon: Database, minRole: 'superadmin' },
        { path: '/audit-trail', label: t('nav.auditTrail'), icon: ClipboardList },
        // Retirada T5 (2026-10-13): la pantalla ApiDocs era una lista a mano y
        // desfasada. Se enlaza la especificacion que sirve FastAPI de verdad
        // (backend/server.py: DOCS_URL = "/api/docs", OPENAPI_URL = "/api/openapi.json").
        { path: '/api/docs', label: t('nav.apiDocs'), icon: FileText, external: true },
      ]
    },
    {
      type: 'link',
      path: '/documentation',
      label: t('nav.docCenter'),
      icon: BookOpen
    },
    {
      type: 'link',
      path: '/changelog',
      label: t('nav.whatsNew'),
      icon: Sparkle,
      secondary: true
    },
    {
      type: 'link',
      path: '/download',
      label: t('common.download'),
      icon: Download
    }
  ];

  const isSubmenuActive = (items) => {
    return items.some(item => location.pathname === item.path);
  };

  // Antes solo vaciaba sessionStorage: el token seguía en localStorage y seguía
  // valiendo. Ahora el servidor lo revoca y el navegador olvida la sesión.
  const handleLogout = async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch {
      // silencio-ok: token ya caducado o red caída; igualmente se borra la sesión local y se va al login
    }
    logout();
    sessionStorage.clear();
    navigate('/login');
  };

  const handleMenuToggle = (menuLabel) => {
    setOpenMenu(openMenu === menuLabel ? null : menuLabel);
  };

  const closeAllMenus = () => {
    setOpenMenu(null);
    setMobileMenuOpen(false);
  };

  const renderLink = (link, inDropdown = false) => {
    const Icon = link.icon;
    const isActive = !link.external && location.pathname === link.path;
    // `external: true` -> no es una ruta de React Router, sino una URL que sirve el
    // backend (p.ej. la especificacion OpenAPI en /api/docs). Se abre en otra pestana.
    const Anchor = link.external ? 'a' : Link;
    const anchorProps = link.external
      ? { href: link.path, target: '_blank', rel: 'noopener noreferrer' }
      : { to: link.path };

    return (
      <Anchor
        key={link.path}
        {...anchorProps}
        data-testid={`nav-${link.path.replace(/^\//, '').replace(/\//g, '-') || 'dashboard'}`}
        onClick={closeAllMenus}
        className={`
          relative flex items-center gap-3 px-3 py-2 rounded-lg text-[13.5px] font-medium transition-all duration-200
          ${inDropdown ? 'w-full' : ''}
          ${isActive
            ? 'text-v4-txt font-semibold bg-v4-panel-2'
            : 'text-v4-dim hover:text-v4-txt hover:bg-v4-panel-2'
          }
        `}
      >
        {isActive && (
          <span aria-hidden="true" className="absolute left-0 top-[6px] bottom-[6px] w-[2px] rounded-r"
            style={{ background: 'hsl(var(--v4-accent))' }}></span>
        )}
        <Icon size={16} className={`flex-shrink-0 ${isActive ? 'text-v4-txt' : 'text-v4-faint'}`} />
        {!sidebarCollapsed && (
          <>
            <span className="flex-1">{link.label}</span>
            {link.badge > 0 && (
              <span className={`
                px-1.5 py-0.5 text-[10px] rounded font-semibold font-mono
                ${link.badgeColor === 'red'
                  ? 'bg-v4-accent-bg text-v4-accent'
                  : 'bg-v4-panel-2 text-v4-dim border border-v4-line-2'
                }
              `}>
                {link.badge}
              </span>
            )}
            {link.isNew && (
              <span className="px-1.5 py-0.5 text-[9px] rounded font-mono font-bold uppercase tracking-wider bg-v4-panel-2 text-v4-dim border border-v4-line-2">
                {t('common.new')}
              </span>
            )}
          </>
        )}
      </Anchor>
    );
  };

  const renderDropdown = (menu) => {
    const Icon = menu.icon;
    const isOpen = openMenu === menu.label;
    const hasActiveChild = isSubmenuActive(menu.items);

    return (
      <div key={menu.label} className="relative">
        <button
          type="button"
          onClick={() => handleMenuToggle(menu.label)}
          data-testid={`nav-dropdown-${(menu.items?.[0]?.path?.split('/')[1] || menu.label).toLowerCase().replace(/\s+/g, '-')}`}
          aria-label={menu.label}
          aria-expanded={isOpen}
          className={`
            w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200
            border-l-2
            ${hasActiveChild || isOpen
              ? 'bg-v4-panel-2 text-v4-txt font-semibold border-v4-accent pl-[10px]'
              : 'text-v4-dim hover:text-v4-txt hover:bg-v4-panel-2 border-transparent pl-[10px]'
            }
          `}
        >
          <Icon size={16} className={`flex-shrink-0 ${hasActiveChild ? 'text-v4-txt' : 'text-v4-faint'}`} />
          {!sidebarCollapsed && (
            <>
              <span className="flex-1 text-left">{menu.label}</span>
              <ChevronRight size={14} className={`text-v4-faint transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
            </>
          )}
        </button>

        {isOpen && !sidebarCollapsed && (
          <div className="mt-0.5 ml-3 pl-3 border-l border-v4-line-2 space-y-0.5">
            {menu.items.map(item => renderLink(item, true))}
          </div>
        )}
      </div>
    );
  };

  // Signal Pulse: ritmo del logo segun actividad operacional
  const totalActive = (stats?.criticalVulns || 0) + (stats?.alerts || 0);
  const pulseSpeed = totalActive >= 10 ? '0.8s' : totalActive >= 3 ? '1.5s' : totalActive >= 1 ? '2.5s' : '4s';
  const pulseColor = totalActive >= 3 ? 'rgba(248, 113, 113, 0.45)' : 'rgba(34, 211, 238, 0.35)';
  const pulseRitmo = totalActive >= 10 ? 'critico' : totalActive >= 3 ? 'elevado' : totalActive >= 1 ? 'activo' : 'calmo';
  const pulseTitle = `${totalActive} senales activas - ritmo ${pulseRitmo}`;

  return (
    <>
      {/* Sidebar */}
      <aside className={`
        sidebar-nav fx-focus
        fixed left-0 top-0 h-full z-50
        bg-[var(--bg-base)] border-r border-v4-line-2
        transition-[width,transform] duration-300
        ${sidebarCollapsed ? 'w-16' : 'w-64'}
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}
      style={{ transitionTimingFunction: 'var(--ease-out-expo)' }}>

        {/* Logo Section — v4 Falcon mark (graphite ring + red core) */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-v4-line">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2.5">
              <svg className="w-7 h-7 flex-shrink-0" viewBox="0 0 28 28" fill="none" title={pulseTitle}
                   style={{ '--pulse-color': pulseColor }}>
                <circle cx="14" cy="14" r="11.5" stroke="hsl(var(--v4-txt))" strokeWidth="1.3" />
                <path d="M14 3.5v6M14 18.5v6M3.5 14h6M18.5 14h6" stroke="hsl(var(--v4-txt))" strokeWidth="1.3" />
                <circle cx="14" cy="14" r="2.4" fill="hsl(var(--v4-accent))">
                  <animate attributeName="opacity" values="1;0.4;1" dur={pulseSpeed} repeatCount="indefinite" />
                </circle>
              </svg>
              <div className="leading-none">
                <h1 className="text-[14px] font-semibold tracking-[0.02em] text-v4-txt">VEKTOR</h1>
                <p className="text-[9px] font-mono uppercase tracking-[0.22em] text-v4-faint mt-1">SOC · Platform</p>
              </div>
            </div>
          )}
          {sidebarCollapsed && (
            <svg className="w-7 h-7 mx-auto" viewBox="0 0 28 28" fill="none" title={pulseTitle}>
              <circle cx="14" cy="14" r="11.5" stroke="hsl(var(--v4-txt))" strokeWidth="1.3" />
              <path d="M14 3.5v6M14 18.5v6M3.5 14h6M18.5 14h6" stroke="hsl(var(--v4-txt))" strokeWidth="1.3" />
              <circle cx="14" cy="14" r="2.4" fill="hsl(var(--v4-accent))">
                <animate attributeName="opacity" values="1;0.4;1" dur={pulseSpeed} repeatCount="indefinite" />
              </circle>
            </svg>
          )}
        </div>

        {/* Client Switcher (C8) — reemplaza el Tenant Selector legacy */}
        <ClientSwitcher collapsed={sidebarCollapsed} />

        {/* Navigation Menu */}
        <nav className="flex-1 px-2 py-2 space-y-0.5 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          {menuItems
            // `userRole === 'admin'` dejaba al superadmin SIN el menú de
            // administración (mismo criterio que ProtectedRoute: admin o superadmin).
            .filter(item => !item.adminOnly || ['admin', 'superadmin'].includes(userRole))
            // QA #6: ocultar entradas SOC al rol `user` (lectura SOC = mínimo viewer).
            .filter(item => hasMinRole(item.minRole))
            .map((item, index) => {
              if (item.type === 'divider') {
                return !sidebarCollapsed ? (
                  <div key={`divider-${index}`} className="px-3 pt-5 pb-2">
                    <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-v4-faint">
                      {item.label}
                    </span>
                  </div>
                ) : null;
              } else if (item.type === 'link') {
                return renderLink(item);
              } else if (item.type === 'dropdown') {
                // Filtrar los items internos por rol; si el dropdown queda vacío, no renderizarlo.
                const visibleItems = (item.items || []).filter(sub => hasMinRole(sub.minRole));
                if (visibleItems.length === 0) return null;
                return renderDropdown({ ...item, items: visibleItems });
              }
              return null;
            })}
        </nav>

        {/* Bottom Section */}
        <div className="absolute bottom-0 left-0 right-0 p-2.5 border-t border-v4-line bg-[var(--bg-base)]">
          {!sidebarCollapsed && (
            <div className="flex items-center justify-between mb-2">
              <ThemeSwitcher />
              <button
                type="button"
                onClick={() => setSidebarCollapsed(true)}
                className="p-1.5 text-v4-faint hover:text-v4-txt hover:bg-v4-panel-2 rounded-md transition-colors"
                title="Collapse sidebar"
                aria-label="Collapse sidebar"
              >
                <PanelLeftClose size={15} />
              </button>
            </div>
          )}

          {sidebarCollapsed && (
            <button
              type="button"
              onClick={() => setSidebarCollapsed(false)}
              aria-label="Expand sidebar"
              title="Expand sidebar"
              className="w-full p-2 text-v4-faint hover:text-v4-txt hover:bg-v4-panel-2 rounded-md transition-colors mb-2"
            >
              <PanelLeft size={15} className="mx-auto" />
            </button>
          )}

          {/* Help / Tour Button */}
          {onStartTour && (
            <button
              type="button"
              onClick={onStartTour}
              className={`
                w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium mb-1
                text-v4-dim hover:text-v4-txt hover:bg-v4-panel-2 transition-all duration-200
                ${sidebarCollapsed ? 'justify-center' : ''}
              `}
              title={t('nav.startGuidedTour')}
              aria-label={t('nav.startGuidedTour')}
            >
              <HelpCircle size={15} />
              {!sidebarCollapsed && <span>{t('nav.guidedTour')}</span>}
            </button>
          )}

          <button
            type="button"
            onClick={handleLogout}
            aria-label={t('common.signOut')}
            className={`
              w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium
              text-v4-dim hover:text-v4-accent hover:bg-v4-accent-bg transition-all duration-200
              ${sidebarCollapsed ? 'justify-center' : ''}
            `}
          >
            <LogOut size={15} />
            {!sidebarCollapsed && <span>{t('common.signOut')}</span>}
          </button>

          {!sidebarCollapsed && (
            <div className="mt-2 pt-2 border-t border-v4-line flex items-center justify-between px-1">
              <span className="text-[9px] font-mono uppercase tracking-[0.16em] text-v4-faint">v{APP_VERSION}</span>
              <span className="text-[9px] font-mono uppercase tracking-[0.16em] text-v4-faint">BUILD 20260716</span>
            </div>
          )}
        </div>
      </aside>

      {/* Top Bar */}
      <header className={`
        fixed top-0 right-0 h-16 bg-[var(--bg-base)] border-b border-v4-line-2 shadow-topbar z-40
        transition-all duration-300
        left-0
        ${sidebarCollapsed ? 'lg:left-16' : 'lg:left-64'}
      `}>
        <div className="h-full flex items-center justify-between px-6">
          {/* Mobile Menu Button */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle navigation menu"
            aria-expanded={mobileMenuOpen}
            className="lg:hidden p-2 text-v4-dim hover:text-v4-txt"
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>

          {/* Page Title - Can be dynamic based on route */}
          <div className="hidden lg:flex items-center gap-3 leading-tight">
            <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-v4-faint">
              VEKTOR
            </span>
            <span className="text-v4-faint">/</span>
            <h2 className="text-[14px] font-medium text-v4-txt m-0">
              {location.pathname === '/' ? t('dashboard.securityDashboard') :
               location.pathname.split('/')[1].charAt(0).toUpperCase() + location.pathname.split('/')[1].slice(1).replace(/-/g, ' ')}
            </h2>
          </div>

          {/* Search Button */}
          <button
            type="button"
            onClick={() => {
              const event = new KeyboardEvent('keydown', { key: 'k', ctrlKey: true });
              document.dispatchEvent(event);
            }}
            aria-label={t('common.searchEllipsis')}
            className="hidden md:flex items-center gap-2.5 px-3 py-1.5 bg-v4-panel-2 border border-v4-line-2 rounded-lg text-v4-dim hover:text-v4-txt hover:border-v4-faint transition-colors"
          >
            <Search className="h-4 w-4" />
            <span className="text-[13px]">{t('common.searchEllipsis')}</span>
            <kbd className="ml-1 px-1.5 py-0.5 text-[10px] font-mono bg-v4-panel rounded border border-v4-line-2 text-v4-dim">⌘K</kbd>
          </button>

          {/* Right Actions */}
          <div className="flex items-center gap-4">
            {/* Status Indicator */}
            {statsError ? (
              <div className="hidden md:flex items-center gap-2" role="alert" title={statsError} data-testid="nav-stats-error">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400"></span>
                <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-red-300">Sin conexión con el servidor</span>
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-v4-ok animate-pulse"></span>
                <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-v4-dim">{t('common.systemOnline')}</span>
              </div>
            )}

            {/* Quick Stats */}
            <div className="hidden lg:flex items-center gap-4">
              <div className="text-right leading-tight">
                <div className="text-[9px] font-mono uppercase tracking-[0.16em] text-v4-faint">AGENTS</div>
                <div className="text-[13px] font-semibold tabular-nums text-v4-txt">{statsError ? '—' : stats.agents}</div>
              </div>
              <div className="w-px h-8 bg-v4-line-2"></div>
              <div className="text-right leading-tight">
                <div className="text-[9px] font-mono uppercase tracking-[0.16em] text-v4-faint">CRIT.</div>
                <div className="text-[13px] font-semibold tabular-nums text-v4-accent">{statsError ? '—' : stats.criticalVulns}</div>
              </div>
            </div>

            {/* View Mode Toggle */}
            <ViewModeToggle />

            {/* Tour Launcher — botón persistente topbar */}
            <TourLauncher
              onStartTour={onStartTour}
              collapsed={false}
            />

            {/* Notification Center */}
            <NotificationCenter />

            {/* User Avatar — v4 graphite tile */}
            <div className="w-9 h-9 rounded-md flex items-center justify-center font-semibold text-[13px] font-mono bg-v4-txt text-v4-panel">
              {sessionStorage.getItem('userEmail')?.charAt(0).toUpperCase() || 'U'}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Content Spacer - This pushes content below the navbar */}
      <div className={`
        transition-all duration-300
        ml-0
        ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}
        pt-16
        min-w-0
      `}></div>
    </>
  );
};

export default Navigation;
